import { downloadWithAuth } from '../../api/authInterceptor';
import { useEffect, useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import {
  checkStatements,
  fetchStatementTemplateInfo,
  reconcileSupplier,
  StatementCheck,
  StatementTemplateInfo,
  supplierTemplateUrl,
  SupplierReconSummary,
} from '../../api/batch3Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  "Upload the Xero Bill Activity ledger (one supplier, .xlsx) and that supplier's statement PDFs.",
  "If a PDF can't be read, the app says so; upload that statement in Excel instead (template provided).",
  "The period is read from the ledger's title line unless you override it. Click Run Reconciliation.",
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function SupplierReconPanel({ mission, onBack }: PanelProps) {
  const [ledger, setLedger] = useState<File[]>([]);
  const [pdfs, setPdfs] = useState<File[]>([]);
  const [excelStatements, setExcelStatements] = useState<File[]>([]);
  const [checks, setChecks] = useState<Record<string, StatementCheck>>({});
  const [templateInfo, setTemplateInfo] = useState<StatementTemplateInfo | null>(null);
  const [usePeriod, setUsePeriod] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const { status, error, result, fail, run } = useFileRun<FileResult<SupplierReconSummary>>();

  useEffect(() => {
    fetchStatementTemplateInfo().then(setTemplateInfo).catch(() => setTemplateInfo(null));
  }, []);

  // Check each newly added PDF once, so unreadable ones are flagged before the run.
  useEffect(() => {
    const todo = pdfs.filter((f) => !checks[`${f.name}:${f.size}`]);
    if (todo.length === 0) return;
    checkStatements(todo)
      .then((rows) =>
        setChecks((prev) => {
          const next = { ...prev };
          todo.forEach((f, i) => {
            next[`${f.name}:${f.size}`] = rows[i];
          });
          return next;
        })
      )
      .catch(() => undefined);
  }, [pdfs, checks]);

  const unreadable = pdfs.filter((f) => checks[`${f.name}:${f.size}`] && !checks[`${f.name}:${f.size}`].readable);

  const handleSubmit = async () => {
    const statements = [...pdfs, ...excelStatements];
    if (ledger.length === 0 || statements.length === 0) {
      fail('Upload the Xero ledger and at least one statement (PDF, or Excel using the template).');
      return;
    }
    if (usePeriod && (!start || !end)) {
      fail('Choose both a start and an end date, or untick "Use custom period".');
      return;
    }
    await run(() => reconcileSupplier(
      ledger[0],
      statements,
      usePeriod ? { start, end } : null
    ), 'Reconciliation failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <FileDrop
        label="Xero Bill Activity ledger"
        required
        accept=".xlsx,.xls"
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={ledger}
        onFiles={setLedger}
      />
      <FileDrop
        label="Supplier statement PDFs"
        required
        multiple
        accept=".pdf"
        hint="PDF statements"
        accentColor={mission.color}
        files={pdfs}
        onFiles={setPdfs}
      />
      {unreadable.map((f) => (
        <div key={f.name + f.size} className="mb-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          ❌ <strong>{f.name}</strong>: PDF not readable ({checks[`${f.name}:${f.size}`].reason}). Upload this
          statement in Excel format instead (template below).
        </div>
      ))}

      <details className="mb-4 rounded-lg border border-border bg-muted px-4 py-3" open={unreadable.length > 0}>
        <summary className="cursor-pointer text-sm font-semibold text-foreground">
          📄 Statement in Excel, for PDFs that can't be read
        </summary>
        <p className="mt-2 text-sm text-muted-foreground">
          Type or paste the statement lines into the template (one row per line), then upload it here. Excel
          statements are used together with any readable PDFs.
        </p>
        <button
          type="button"
          onClick={() =>
            downloadWithAuth(supplierTemplateUrl, 'Supplier_Statement_Template.xlsx').catch(() =>
              window.alert('Could not download the template. Try signing in again.'),
            )
          }
          className="mt-2 inline-block rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
        >
          📥 Download Excel template
        </button>
        {templateInfo && (
          <>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
              {templateInfo.rules.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
            <div className="mt-3 overflow-x-auto rounded-md border border-border bg-background">
              <table className="w-full text-left text-xs">
                <thead className="bg-muted text-muted-foreground">
                  <tr>
                    {templateInfo.columns.map((c) => (
                      <th key={c} className="px-3 py-1.5 font-semibold">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {templateInfo.example.map((row, i) => (
                    <tr key={i} className="border-t border-border">
                      {row.map((cell, j) => (
                        <td key={j} className="px-3 py-1.5 text-foreground">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="mt-4">
          <FileDrop
            label="Statement Excel files"
            multiple
            accept=".xlsx,.xlsm,.xls"
            hint="Excel statements"
            accentColor={mission.color}
            files={excelStatements}
            onFiles={setExcelStatements}
          />
        </div>
      </details>

      <details className="mb-6 rounded-lg border border-border bg-muted px-4 py-3">
        <summary className="cursor-pointer text-sm font-semibold text-foreground">Override period (optional)</summary>
        <label className="mt-3 flex items-center gap-2 text-sm text-foreground">
          <input type="checkbox" checked={usePeriod} onChange={(e) => setUsePeriod(e.target.checked)} />
          Use custom period
        </label>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="sr-from" className="mb-1.5">
              From
            </Label>
            <Input id="sr-from" type="date" disabled={!usePeriod} value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="sr-to" className="mb-1.5">
              To
            </Label>
            <Input id="sr-to" type="date" disabled={!usePeriod} value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
      </details>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Reconciling…' : 'Run Reconciliation'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Reconciling…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
            ✅ {result.filename} downloaded{s ? ` (${s.period})` : ''}.
          </div>
          {s?.messages.map((m, i) => (
            <div
              key={i}
              className={`rounded-lg px-4 py-3 ${
                m.level === 'success' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'
              }`}
            >
              {m.text}
            </div>
          ))}
          {s && s.log.length > 0 && (
            <details className="rounded-lg border border-border bg-muted px-4 py-3 text-muted-foreground">
              <summary className="cursor-pointer text-foreground">📋 Processing log</summary>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                {s.log.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {status === 'idle' && (
        <InfoNotice>📁 Upload the Xero ledger and at least one statement (PDF, or Excel using the template).</InfoNotice>
      )}
    </MissionShell>
  );
}

export default SupplierReconPanel;
